import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tv } from "tailwind-variants";

import { useDebouncedState } from "../../hooks/useDebouncedState";
import { useDomRef } from "../../hooks/useDomRef";
import { cn } from "../../utils/cn";
import { Tag } from "./Tag";

import type { ChangeEvent, HTMLAttributes, KeyboardEvent, ReactNode } from "react";
import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";
import type { TagColorVariant } from "./Tag";

export interface TagItem {
  id: string;
  label: string;
  // Optional properties
  color?: TagColorVariant;
  isPrepopulated?: boolean;
  isStaged?: boolean;
  data?: unknown;
  // Icon support
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  // Tooltip support
  tooltipContent?: ReactNode;
  showTooltip?: boolean;
}

// Only define styling for container, input, and dropdown. Tag's styling is handled by that component.
const tagAutocomplete = tv({
  slots: {
    base: [
      "relative flex min-h-[48px] w-full flex-wrap items-center gap-2",
      "rounded-lg border border-gray-300 bg-white p-2",
      "focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-300",
    ],
    input: [
      "min-w-[120px] flex-1 border-none px-2 py-1.5 text-sm outline-none",
      "bg-white placeholder:text-gray-400",
    ],
    dropdown: [
      "absolute inset-x-0 top-full z-10 mt-1",
      "max-h-72 overflow-y-auto",
      "rounded-md border border-gray-200 bg-white shadow-lg",
      "py-2",
    ],
    dropdownItem: ["flex cursor-pointer items-center gap-2 px-4 py-3 text-sm", "hover:bg-gray-50"],
    dropdownEmptyState: ["px-4 py-3 text-sm italic text-gray-500"],
    dropdownLoadingState: ["px-4 py-3 text-sm text-gray-500"],
  },
  variants: {
    size: {
      sm: { base: "min-h-[40px] text-xs", input: "px-1.5 py-1 text-xs" },
      md: { base: "min-h-[48px] text-sm", input: "px-2 py-1.5 text-sm" },
      lg: { base: "min-h-[56px] text-base", input: "px-2.5 py-2 text-base" },
    },
    disabled: {
      true: { base: "pointer-events-none bg-gray-50 opacity-60" },
    },
  },
  defaultVariants: {
    size: "md",
    disabled: false,
  },
});

type TagAutocompleteClassNames = SlotsToClasses<keyof ReturnType<typeof tagAutocomplete>>;

export interface TagAutocompleteProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange" | "onSelect">,
    Omit<VariantProps<typeof tagAutocomplete>, ""> {
  // Core props
  tags: TagItem[];
  suggestions?: TagItem[];
  onTagsChange: (tags: TagItem[]) => void;

  // Optional props
  isLoading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  size?: "sm" | "md" | "lg";
  classNames?: TagAutocompleteClassNames;

  // Callbacks
  onInputChange?: (value: string) => void;
  onSearch?: (value: string) => void;
  onTagRemove?: (tag: TagItem) => void;
  onTagAdd?: (tag: TagItem) => void;

  // Customization
  renderTag?: (tag: TagItem, onRemove: () => void) => ReactNode;
  renderSuggestion?: (tag: TagItem, onSelect: () => void) => ReactNode;
  noSuggestionsContent?: ReactNode;
  loadingContent?: ReactNode;

  // Color customization for different tag states
  newTagColor?: TagColorVariant;
  stagedTagColor?: TagColorVariant;
  prepopulatedTagColor?: TagColorVariant;
  stagedTagClassName?: string;

  // Checkbox selection
  showCheckboxes?: boolean;
}

// Optimized memoized component for rendering individual tags
const TagItem = React.memo<{
  tag: TagItem;
  onRemove: (tag: TagItem) => void;
  renderTag?: (tag: TagItem, onRemove: () => void) => ReactNode;
  tagColor: TagColorVariant;
  className?: string;
  title?: string;
}>(({ tag, onRemove, renderTag, tagColor, className, title }) => {
  const handleRemove = useCallback(() => onRemove(tag), [onRemove, tag]);

  const tagContent = useMemo(
    () => (
      <div className="flex items-center justify-center gap-1.5">
        {tag.icon && tag.iconPosition !== "right" && tag.icon}
        <span>{tag.label}</span>
        {tag.icon && tag.iconPosition === "right" && tag.icon}
      </div>
    ),
    [tag.icon, tag.iconPosition, tag.label]
  );

  if (renderTag) {
    return <>{renderTag(tag, handleRemove)}</>;
  }

  return (
    <Tag color={tagColor} onRemove={handleRemove} className={className} title={title}>
      {tagContent}
    </Tag>
  );
});

TagItem.displayName = "TagItem";

// Optimized memoized component for rendering suggestions
const SuggestionItem = React.memo<{
  suggestion: TagItem;
  onSelect: (suggestion: TagItem) => void;
  renderSuggestion?: (suggestion: TagItem, onSelect: () => void) => ReactNode;
  isActive: boolean;
  isSelected: boolean;
  showCheckboxes: boolean;
  className?: string;
}>(
  ({ suggestion, onSelect, renderSuggestion, isActive, isSelected, showCheckboxes, className }) => {
    const handleSelect = useCallback(() => onSelect(suggestion), [onSelect, suggestion]);

    if (renderSuggestion) {
      return <>{renderSuggestion(suggestion, handleSelect)}</>;
    }

    return (
      <div className={cn(className, isActive && "bg-gray-50")} onClick={handleSelect}>
        {showCheckboxes && (
          <div className="mr-2 flex items-center justify-center">
            <div
              className={cn(
                "flex size-4 items-center justify-center rounded border",
                isSelected ? "border-blue-600 bg-blue-500" : "border-gray-300"
              )}
            >
              {isSelected && (
                <svg
                  className="size-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          </div>
        )}
        <span className="flex-1">{suggestion.label}</span>
      </div>
    );
  }
);

SuggestionItem.displayName = "SuggestionItem";

export const TagAutocomplete = React.memo(
  forwardRef<HTMLDivElement, TagAutocompleteProps>(
    (
      {
        tags,
        suggestions = [],
        onTagsChange,
        isLoading = false,
        disabled = false,
        placeholder = "Type to search...",
        size = "md",
        classNames,
        onInputChange,
        onSearch,
        onTagRemove,
        onTagAdd,
        renderTag,
        renderSuggestion,
        noSuggestionsContent = "No suggestions",
        loadingContent = "Loading...",
        className,
        // Color customization props
        newTagColor,
        stagedTagColor,
        prepopulatedTagColor,
        stagedTagClassName,
        // Checkbox selection
        showCheckboxes = false,
        ...props
      },
      ref
    ) => {
      const domRef = useDomRef(ref);
      const containerRef = useRef<HTMLDivElement>(null);
      const inputRef = useRef<HTMLInputElement>(null);
      const dropdownRef = useRef<HTMLDivElement>(null);

      const [inputValue, setInputValue] = useState("");
      const [showDropdown, setShowDropdown] = useState(false);
      const [activeIndex, setActiveIndex] = useState(-1);

      // Debounced search to avoid excessive API calls
      const [, setDebouncedValue] = useDebouncedState(
        inputValue,
        value => {
          if (onSearch) {
            onSearch(value);
          }
        },
        { wait: 500 }
      );

      // Split tags into regular and staged for removal
      const { regularTags, stagedTags } = useMemo(() => {
        const regular: TagItem[] = [];
        const staged: TagItem[] = [];

        tags.forEach(tag => {
          if (tag.isStaged) {
            staged.push(tag);
          } else {
            regular.push(tag);
          }
        });

        return { regularTags: regular, stagedTags: staged };
      }, [tags]);

      // Filter out suggestions that are already selected - but only if not using checkboxes
      const filteredSuggestions = useMemo(() => {
        if (showCheckboxes) {
          // When using checkboxes, show all suggestions
          return suggestions;
        }
        // Otherwise filter out already selected items
        return suggestions.filter(suggestion => !tags.some(tag => tag.id === suggestion.id));
      }, [suggestions, tags, showCheckboxes]);

      // Handle input change
      const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
          const value = e.target.value;
          setInputValue(value);
          setDebouncedValue(value);

          if (onInputChange) {
            onInputChange(value);
          }

          setShowDropdown(true);
          setActiveIndex(-1);
        },
        [onInputChange, setDebouncedValue]
      );

      // Handle tag removal
      const handleTagRemove = useCallback(
        (tag: TagItem) => {
          let newTags: TagItem[];

          if (tag.isPrepopulated) {
            // For prepopulated tags, mark as staged for removal
            newTags = tags.map(t => (t.id === tag.id ? { ...t, isStaged: true } : t));
          } else {
            // For user-added tags, remove immediately
            newTags = tags.filter(t => t.id !== tag.id);
          }

          onTagsChange(newTags);

          if (onTagRemove) {
            onTagRemove(tag);
          }
        },
        [tags, onTagsChange, onTagRemove]
      );

      // Handle tag selection from dropdown
      const handleTagSelect = useCallback(
        (tag: TagItem) => {
          // Check if the tag is already in the list
          const existingIndex = tags.findIndex(t => t.id === tag.id);

          let newTags: TagItem[];
          if (existingIndex >= 0) {
            if (tags[existingIndex].isStaged) {
              // If the tag exists but is staged for removal, unstage it
              newTags = tags.map(t => (t.id === tag.id ? { ...t, isStaged: false } : t));
            } else if (showCheckboxes) {
              // If checkboxes are enabled and tag already exists, remove it (toggle behavior)
              newTags = tags.filter(t => t.id !== tag.id);
            } else {
              // If tag already exists and not using checkboxes, do nothing
              newTags = [...tags];
            }
          } else {
            // Otherwise add it as a new tag
            newTags = [...tags, { ...tag, isStaged: false }];
          }

          onTagsChange(newTags);

          // Only clear input and close dropdown if not using checkboxes
          if (!showCheckboxes) {
            setInputValue("");
            setDebouncedValue("");
            setShowDropdown(false);
          }

          if (onTagAdd && existingIndex < 0) {
            onTagAdd(tag);
          }

          // Focus back on the input
          inputRef.current?.focus();
        },
        [tags, onTagsChange, onTagAdd, setDebouncedValue, showCheckboxes]
      );

      // Handle keyboard navigation
      const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
          if (!showDropdown) return;

          switch (e.key) {
            case "ArrowDown":
              e.preventDefault();
              setActiveIndex(prev => (prev < filteredSuggestions.length - 1 ? prev + 1 : prev));
              break;
            case "ArrowUp":
              e.preventDefault();
              setActiveIndex(prev => (prev > 0 ? prev - 1 : prev));
              break;
            case "Enter":
              e.preventDefault();
              if (activeIndex >= 0 && activeIndex < filteredSuggestions.length) {
                handleTagSelect(filteredSuggestions[activeIndex]);
              }
              break;
            case "Escape":
              e.preventDefault();
              setShowDropdown(false);
              break;
            default:
              break;
          }
        },
        [showDropdown, filteredSuggestions, activeIndex, handleTagSelect]
      );

      // Handle input focus
      const handleInputFocus = useCallback(() => {
        setShowDropdown(true);
      }, []);

      // Handle container click
      const handleContainerClick = useCallback(() => {
        inputRef.current?.focus();
      }, []);

      // Handle click outside to close dropdown
      useEffect(() => {
        // Only add event listener if dropdown is shown
        if (!showDropdown) return undefined;

        const handleClickOutside = (e: MouseEvent) => {
          const target = e.target as Node;
          if (containerRef.current?.contains(target) || dropdownRef.current?.contains(target)) {
            return;
          }
          setShowDropdown(false);
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
      }, [showDropdown]);

      // Generate classes
      const slots = tagAutocomplete({ size, disabled });

      return (
        <div
          ref={domRef}
          className={slots.base({ className })}
          onClick={handleContainerClick}
          {...props}
        >
          {/* Render regular tags */}
          {regularTags.map(tag => {
            // Apply color based on tag state
            const tagColor = tag.isPrepopulated
              ? prepopulatedTagColor || tag.color || "neutral"
              : newTagColor || tag.color || "success";

            // Use title attribute for tooltip if needed
            const tooltipContent =
              tag.showTooltip && tag.tooltipContent
                ? typeof tag.tooltipContent === "string"
                  ? tag.tooltipContent
                  : ""
                : undefined;

            return (
              <TagItem
                key={tag.id}
                tag={tag}
                onRemove={handleTagRemove}
                renderTag={renderTag}
                tagColor={tagColor}
                className="py-1"
                title={tooltipContent}
              />
            );
          })}

          {/* Render staged tags */}
          {stagedTags.map(tag => {
            // Apply color for staged tags
            const tagColor = stagedTagColor || tag.color || "danger";
            const stagedClassName = stagedTagClassName || "line-through opacity-60";

            // Use title attribute for tooltip if needed
            const tooltipContent =
              tag.showTooltip && tag.tooltipContent
                ? typeof tag.tooltipContent === "string"
                  ? tag.tooltipContent
                  : ""
                : undefined;

            if (renderTag) {
              return (
                <div key={tag.id} className={stagedClassName}>
                  <TagItem
                    tag={tag}
                    onRemove={handleTagRemove}
                    renderTag={renderTag}
                    tagColor={tagColor}
                    className="py-1"
                    title={tooltipContent}
                  />
                </div>
              );
            }

            // Wrap with staged className
            return (
              <div key={tag.id} className={stagedClassName}>
                <TagItem
                  tag={tag}
                  onRemove={handleTagRemove}
                  tagColor={tagColor}
                  className="py-1"
                  title={tooltipContent}
                />
              </div>
            );
          })}

          {/* Input field */}
          <input
            ref={inputRef}
            type="text"
            className={slots.input({ className: classNames?.input })}
            placeholder={tags.length === 0 ? placeholder : ""}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />

          {/* Dropdown */}
          {showDropdown && (
            <div ref={dropdownRef} className={slots.dropdown({ className: classNames?.dropdown })}>
              {isLoading ? (
                <div className={slots.dropdownLoadingState()}>{loadingContent}</div>
              ) : filteredSuggestions.length > 0 ? (
                filteredSuggestions.map((suggestion, index) => {
                  // Check if the suggestion is already selected
                  const isSelected = tags.some(t => t.id === suggestion.id);

                  return (
                    <SuggestionItem
                      key={suggestion.id}
                      suggestion={suggestion}
                      onSelect={handleTagSelect}
                      renderSuggestion={renderSuggestion}
                      isActive={activeIndex === index}
                      isSelected={isSelected}
                      showCheckboxes={showCheckboxes}
                      className={slots.dropdownItem({ className: classNames?.dropdownItem })}
                    />
                  );
                })
              ) : (
                <div className={slots.dropdownEmptyState()}>{noSuggestionsContent}</div>
              )}
            </div>
          )}
        </div>
      );
    }
  )
);

TagAutocomplete.displayName = "TagAutocomplete";
